import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  SOCKET_CHAT_EVENT,
  SOCKET_CLASSROOM_ATTENDANCE_EVENTS,
  SOCKET_DISCUSSION_EVENTS,
  SOCKET_LTI_EVENTS,
  SOCKET_NAMESPACE,
} from 'constants/constants';
import Socket from 'helpers/socket';
import { SOCKET_URL } from 'config';
import {
  setGroupUnread,
  setMessage,
  setOnlineChange,
  setUpdatedGroup,
  setUpdateUnread,
  setUserUnread,
} from 'store/messages';
import { setUpdateSocket } from 'store/discussion';
import { setSelectedLtiDeepLink } from 'store/lti';
import { setQrAttendanceCount } from 'store/classroomAttendance';
import { PREVIOUS_ROLEID } from 'helpers/storage';
import { useShouldDisconnectSocket, useUser } from './index';

export const useSocket = (namespace = '', params = {}) => {
  const auth = useUser();
  const authId = auth?._id;
  const previousRoleIdRef = useRef();
  const socketRef = useRef();
  const paramsRef = useRef(params);
  const paramsKey = JSON.stringify(params || {});

  const dispatch = useDispatch();
  const shouldDisconnect = useShouldDisconnectSocket();

  const listenEventByNamespace = useCallback(
    (socket) => {
      switch (namespace) {
        case SOCKET_NAMESPACE.CHAT:
          // Listen event Chat
          socket.on(SOCKET_CHAT_EVENT.MESSAGE, (event) => {
            if (event?._id) {
              if (event?.sender?._id) {
                event.sender.online = true;
              }
              dispatch(setMessage(event));
              if (event?.sender?._id && event.sender._id !== authId) {
                dispatch(setUpdateUnread(event));
              }
            }
          });
          socket.on(SOCKET_CHAT_EVENT.USER_ONLINE_STATE_CHANGE, (event) => {
            const data = {
              ...event,
              ...(previousRoleIdRef.current && {
                adminid: previousRoleIdRef.current,
              }),
            };
            dispatch(setOnlineChange(data));
          });
          socket.on(SOCKET_CHAT_EVENT.USER_UNREAD_MESSAGE_NUM, (event) => {
            if (typeof event?.unreadMessage === 'number') {
              dispatch(setUserUnread(event?.unreadMessage));
            }
          });
          socket.on(SOCKET_CHAT_EVENT.GROUP_UNREAD_MESSAGE_NUM, (event) => {
            dispatch(setGroupUnread(event));
          });
          socket.on(SOCKET_CHAT_EVENT.GROUP_CHAT_UPDATE, (event) => {
            dispatch(setUpdatedGroup(event));
          });
          break;
        case SOCKET_NAMESPACE.DISCUSSION:
          // Listen event Discussion
          socket.on(SOCKET_DISCUSSION_EVENTS.DISCUSSION, (event) => {
            dispatch(
              setUpdateSocket({
                type: SOCKET_DISCUSSION_EVENTS.DISCUSSION,
                data: event,
                time: Date.now(),
              }),
            );
          });
          socket.on(SOCKET_DISCUSSION_EVENTS.DISCUSSION_UPDATED, (event) => {
            dispatch(
              setUpdateSocket({
                type: SOCKET_DISCUSSION_EVENTS.DISCUSSION_UPDATED,
                data: event,
                time: Date.now(),
              }),
            );
          });
          socket.on(SOCKET_DISCUSSION_EVENTS.DISCUSSION_DELETED, (event) => {
            dispatch(
              setUpdateSocket({
                type: SOCKET_DISCUSSION_EVENTS.DISCUSSION_DELETED,
                data: event,
                time: Date.now(),
              }),
            );
          });
          break;
        case SOCKET_NAMESPACE.LTI:
          socket.on(SOCKET_LTI_EVENTS.LTI_DEEP_LINK_SELECTED, (event) => {
            dispatch(
              setSelectedLtiDeepLink({
                packages: event?.packages ?? [],
                time: Date.now(),
              }),
            );
          });
          break;
        case SOCKET_NAMESPACE.CLASSROOM_ATTENDANCE:
          socket.on(
            SOCKET_CLASSROOM_ATTENDANCE_EVENTS.COUNT_UPDATED,
            (event) => {
              dispatch(setQrAttendanceCount(event));
            },
          );
          break;
        default:
          break;
      }
    },
    [authId, dispatch, namespace],
  );

  useEffect(() => {
    previousRoleIdRef.current = PREVIOUS_ROLEID.get();
  }, []);

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const handleConnectSocket = useCallback(() => {
    const socket = new Socket(SOCKET_URL + namespace);
    socketRef.current = socket;

    try {
      socket.connect(paramsRef.current);

      // Listen event connection
      socket.on('connect', () => {
        // Todo: event connect
        console.log('socket connected');
      });

      // Listen event by namespace
      listenEventByNamespace(socket);

      // Listen event disconnect
      socket.on('disconnect', () => {
        console.log('socket disconnected');
      });
    } catch (error) {
      console.log(error);
    }

    return socket;
  }, [listenEventByNamespace, namespace]);

  const handleDisconnectSocket = useCallback((socket = socketRef.current) => {
    try {
      if (typeof socket?.disconnect === 'function') {
        if (socket?.socket?.connected) {
          console.log('disconnect socket');
        }
        socket.disconnect();
      }
      if (socketRef.current === socket) {
        socketRef.current = undefined;
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    if (!authId) {
      return undefined;
    }

    const socket = handleConnectSocket();
    return () => handleDisconnectSocket(socket);
  }, [authId, handleConnectSocket, handleDisconnectSocket, paramsKey]);

  useEffect(() => {
    if (shouldDisconnect) {
      handleDisconnectSocket();
    }
  }, [handleDisconnectSocket, shouldDisconnect]);
};
